import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Play, RotateCcw, CheckCircle2, Clock, Target, Zap } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TURKISH_COLORS = [
  { name: 'KIRMIZI', hex: '#EF4444' },
  { name: 'MAVİ', hex: '#3B82F6' },
  { name: 'YEŞİL', hex: '#22C55E' },
  { name: 'SARI', hex: '#EAB308' },
  { name: 'MOR', hex: '#A855F7' },
  { name: 'TURUNCU', hex: '#F97316' },
];

const TOTAL_TRIALS = 20;

function generateTrial() {
  const wordIdx = Math.floor(Math.random() * TURKISH_COLORS.length);
  let colorIdx = Math.floor(Math.random() * TURKISH_COLORS.length);
  // 70% incongruent for Stroop effect
  if (Math.random() < 0.7) {
    while (colorIdx === wordIdx) {
      colorIdx = Math.floor(Math.random() * TURKISH_COLORS.length);
    }
  }
  return {
    word: TURKISH_COLORS[wordIdx].name,
    wordColorName: TURKISH_COLORS[colorIdx].name,
    displayColor: TURKISH_COLORS[colorIdx].hex,
    correctAnswer: TURKISH_COLORS[colorIdx].name,
  };
}

export default function StroopTask({ onBack }) {
  const [phase, setPhase] = useState('intro'); // intro, playing, result
  const [trialIndex, setTrialIndex] = useState(0);
  const [currentTrial, setCurrentTrial] = useState(null);
  const [trials, setTrials] = useState([]);
  const [trialStartTime, setTrialStartTime] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const feedbackTimeout = useRef(null);

  const startGame = useCallback(() => {
    setTrials([]);
    setTrialIndex(0);
    setCurrentTrial(generateTrial());
    setTrialStartTime(Date.now());
    setFeedback(null);
    setResult(null);
    setPhase('playing');
  }, []);

  const handleAnswer = useCallback((selectedColor) => {
    if (!currentTrial || feedback) return;
    const rt = Date.now() - trialStartTime;
    const correct = selectedColor === currentTrial.correctAnswer;
    const trialData = {
      word: currentTrial.word,
      color: currentTrial.wordColorName,
      selected: selectedColor,
      correct,
      reaction_time_ms: rt,
    };
    const updatedTrials = [...trials, trialData];
    setTrials(updatedTrials);
    setFeedback(correct ? 'correct' : 'incorrect');

    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = setTimeout(() => {
      setFeedback(null);
      if (updatedTrials.length >= TOTAL_TRIALS) {
        submitResults(updatedTrials);
      } else {
        setTrialIndex(prev => prev + 1);
        setCurrentTrial(generateTrial());
        setTrialStartTime(Date.now());
      }
    }, 400);
  }, [currentTrial, feedback, trials, trialStartTime]);

  useEffect(() => {
    return () => { if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current); };
  }, []);

  const submitResults = async (allTrials) => {
    setSubmitting(true);
    const correctCount = allTrials.filter(t => t.correct).length;
    const avgRt = allTrials.reduce((s, t) => s + t.reaction_time_ms, 0) / allTrials.length;
    const payload = {
      total_trials: allTrials.length,
      correct_count: correctCount,
      incorrect_count: allTrials.length - correctCount,
      average_reaction_time_ms: avgRt,
      trials: allTrials,
    };
    try {
      const res = await axios.post(`${API}/api/cognitive/stroop/submit`, payload);
      setResult(res.data);
      setPhase('result');
    } catch {
      setResult({ label: 'Hata', description: 'Sonuç kaydedilemedi.', color: 'red', accuracy_pct: ((correctCount / allTrials.length) * 100).toFixed(1), avg_reaction_time_ms: avgRt.toFixed(0) });
      setPhase('result');
    }
    setSubmitting(false);
  };

  const colorMap = { emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' }, cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' }, amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' }, orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' }, red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' } };

  // INTRO
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-lg w-full" data-testid="stroop-intro">
          <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white/80 mb-6 transition" data-testid="stroop-back">
            <ArrowLeft className="w-4 h-4" /> Geri Dön
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Stroop Görevi</h1>
              <p className="text-white/50 text-sm">Dikkat ve İnhibisyon Testi</p>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed mb-4">
            Ekranda bir renk ismi görünecek, ancak yazı farklı bir renkte olacak.
            Göreviniz <span className="text-cyan-300 font-semibold">yazının rengini</span> seçmek,
            <span className="text-white/40"> kelimeyi değil</span>.
          </p>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
            <p className="text-white/50 text-xs mb-2">Örnek:</p>
            <p className="text-3xl font-black text-center mb-2" style={{ color: '#3B82F6' }}>KIRMIZI</p>
            <p className="text-white/40 text-xs text-center">Doğru cevap: <span className="text-blue-400 font-semibold">MAVİ</span> (yazının rengi)</p>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/50 mb-6">
            <span className="flex items-center gap-1"><Target className="w-4 h-4" /> {TOTAL_TRIALS} deneme</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> ~2 dakika</span>
          </div>
          <button onClick={startGame} className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2" data-testid="stroop-start">
            <Play className="w-5 h-5" /> Başla
          </button>
        </div>
      </div>
    );
  }

  // PLAYING
  if (phase === 'playing' && currentTrial) {
    const progress = ((trialIndex + 1) / TOTAL_TRIALS) * 100;
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6" data-testid="stroop-game">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-4 text-sm text-white/50">
            <span>{trialIndex + 1} / {TOTAL_TRIALS}</span>
            <span>Yazının <span className="text-cyan-300 font-semibold">rengini</span> seçin</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full mb-8">
            <div className="h-full bg-purple-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>

          <div className={`bg-white/5 border rounded-2xl p-8 text-center mb-6 transition-all ${
            feedback === 'correct' ? 'border-emerald-500/50 bg-emerald-500/5' :
            feedback === 'incorrect' ? 'border-red-500/50 bg-red-500/5' : 'border-white/10'
          }`}>
            <p className="text-5xl sm:text-6xl font-black select-none" style={{ color: currentTrial.displayColor }} data-testid="stroop-stimulus">
              {currentTrial.word}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {TURKISH_COLORS.map(c => (
              <button key={c.name}
                onClick={() => handleAnswer(c.name)}
                disabled={!!feedback}
                className="py-3 px-2 rounded-xl font-bold text-sm transition-all border border-white/10 hover:border-white/30 active:scale-95 disabled:opacity-50"
                style={{ backgroundColor: c.hex + '22', color: c.hex }}
                data-testid={`stroop-btn-${c.name}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // RESULT
  if (phase === 'result' && result) {
    const c = colorMap[result.color] || colorMap.amber;
    return (
      <div className="min-h-screen bg-[#0f172a] py-8 px-4" data-testid="stroop-result">
        <div className="max-w-lg mx-auto space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="text-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white">Stroop Görevi Sonucu</h2>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`${c.bg} border ${c.border} rounded-xl p-4 text-center`}>
                <p className={`text-2xl font-bold ${c.text}`}>{result.accuracy_pct}%</p>
                <p className="text-white/40 text-xs mt-1">Doğruluk</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{Math.round(result.avg_reaction_time_ms)} ms</p>
                <p className="text-white/40 text-xs mt-1">Ort. Tepki Süresi</p>
              </div>
            </div>

            <div className="text-center mb-4">
              <span className={`${c.bg} ${c.text} px-4 py-1.5 rounded-full text-sm font-medium border ${c.border}`}>
                {result.label}
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed text-center">{result.description}</p>
            {result.measures && <p className="text-white/30 text-xs text-center mt-3">Ölçülen: {result.measures}</p>}
          </div>

          <div className="flex gap-3">
            <button onClick={startGame} className="flex-1 py-3 bg-purple-500/20 text-purple-300 rounded-xl font-medium transition hover:bg-purple-500/30 flex items-center justify-center gap-2" data-testid="stroop-retry">
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={onBack} className="flex-1 py-3 bg-white/10 text-white/70 rounded-xl font-medium transition hover:bg-white/15" data-testid="stroop-done">
              Tamamla
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
